import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { httpOk, GET, POST, FILE } from 'helpers/api'
import { notify } from 'helpers/helpers'
import useFileDrop from 'pages/parts/useFileDrop'
import BrandBanners from './BrandBanners'
import BrandLinks from './BrandLinks'

function Template() {
	const { t } = useTranslation();

	const [data, setData] = useState({})

	async function updateBrand() {
		var response = await POST('/brands', data, { loader: true, notification: true })

		if (httpOk(response)) return
	}

	async function uploadImageFile(file) {
		if (!file) return

		if (!file.type?.startsWith('image/')) {
			notify(t('image2'))
			return
		}

		let formData = new FormData();
		formData.append("image", file);
		formData.append("path", 'brands');

		var dataCopy = { ...data }
		var response = null;
		response = await FILE(`/upload-image-only`, formData, {}, { loader: true });
		if (httpOk(response)) {
			dataCopy.image = response.data.url
			dataCopy.logo64 = response.data.base64
			setData(dataCopy)
		}
	}

	async function fileUpload(e) {
		await uploadImageFile(e.target.files[0])
	}

	const { dropProps, dropStyle } = useFileDrop(uploadImageFile)

	function handleChangeCheckbox(key) {
		var dataCopy = { ...data }
		dataCopy[key] = !dataCopy[key]

		setData(dataCopy)
	}

	function handleChangeInput(e) {
		var dataCopy = { ...data }
		dataCopy[e.target.name] = e.target.value

		setData(dataCopy)
	}

	async function getBrand() {
		const response = await GET('/brands')
		if (!httpOk(response)) return

		// Перенос остатка по зарплате включён по умолчанию: пока бэкенд не отдаёт поле,
		// переключатель не должен выглядеть выключённым
		setData({
			...response.data,
			'is_salary_saldo_enabled': response.data?.is_salary_saldo_enabled !== false,
		})
	}

	useEffect(() => {
		getBrand()
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const brandSettings = [
		{ label: t('hall'), key: 'is_hall_enabled' },
		{ label: t('takeaway'), key: 'is_takeaway_enabled' },
		{ label: t('delivery'), key: 'is_delivery_enabled' },
		{ label: t('show_sale_price'), key: 'is_price_visible' },
		{ label: t('show_product_image'), key: 'is_image_visible' },
		{ label: t('guests_selection'), key: 'is_guest_count_enabled' },
		{ label: t('split_products'), key: 'is_products_ungrouped' },
		{ label: t('button_monitor'), key: 'is_order_collection_enabled' },
		{ label: t('button_prepayment'), key: 'is_prepayment_enabled' },
		{ label: t('fast_food_mode'), key: 'is_fast_food_mode_enabled' },
		{ label: t('online_stop_list'), key: 'is_online_stop_list_enabled' },
		{ label: t('cheque_online'), key: 'is_online_cheque_enabled' },
		{ label: t('change_delivery_price'), key: 'is_delivery_price_editable' },
		{ label: t('deletion_reason'), key: 'is_deletion_reason_enabled' },
		// Выключено — зарплата за период считается сама по себе: долг и переплата
		// с прошлых периодов не переносятся, колонка «Остаток на начало» скрыта
		{ label: t('salary_saldo_carryover'), key: 'is_salary_saldo_enabled', group: 'shift' },
	]

	// Переключатели без группы — настройки кассы, их большинство
	const cashboxSettings = brandSettings.filter(item => !item.group)
	const shiftSettings = brandSettings.filter(item => item.group === 'shift')

	function renderToggle(item) {
		return (
			<div
				key={item.key}
				className="d-flex justify-content-between w-100 brand-settings-row cursor-pointer"
				role="button"
				tabIndex={0}
				onClick={() => handleChangeCheckbox(item.key)}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleChangeCheckbox(item.key)
				}}>
				<h6>{item.label}</h6>
				<div className="form-check form-switch form-switch-sm">
					<input type="checkbox" className="form-check-input" name={item.key}
						checked={data[item.key] ?? false}
						onClick={(e) => e.stopPropagation()}
						onChange={() => handleChangeCheckbox(item.key)} />
				</div>
			</div>
		)
	}

	// Режим закрытия смены при открытых чеках. Список, а не переключатель:
	// сюда добавятся режимы вроде «разрешить по PIN менеджера»
	const shiftCloseModes = [
		{ 'id': 'allow', 'name': t('shift_close_mode_allow') },
		{ 'id': 'block', 'name': t('shift_close_mode_block') },
	]

	// Вид экрана открытых чеков на кассе. Умолчание заведения — кассир может
	// переключить под себя, его выбор хранится на кассе и перебивает это
	const cashboxLayouts = [
		{ 'id': 'classic', 'name': t('cashbox_layout_classic') },
		{ 'id': 'simple', 'name': t('cashbox_layout_simple') },
	]

	// День рождения гостя в боте, на сайте и в приложении. Указанный ДР гость
	// сам больше не меняет — исправляет сотрудник с правом «Исправлять день рождения»
	const birthdayModes = [
		{ 'id': 'off', 'name': t('birthday_mode_off') },
		{ 'id': 'optional', 'name': t('birthday_mode_optional') },
		{ 'id': 'required', 'name': t('birthday_mode_required') },
	]

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center h-100">
					{t('brand')}
				</h4>
				<button className="btn btn-primary"
					onClick={() => updateBrand()}>
					{t('save')}
				</button>
			</div>

			<div className="card w-100">
				<div className="card-body">
					<div className="row">
						<div className="col-md-8">
							<div className="d-flex flex-column">
								<h5 className="brand-settings-group">{t('brand_group_cashbox')}</h5>
								{cashboxSettings.map(renderToggle)}

								<div className="d-flex justify-content-between w-100 brand-settings-row brand-settings-row--control">
									<div className="brand-settings-row__label">
										<h6>{t('cashbox_layout')}</h6>
									</div>
									<div className="brand-settings-row__control">
										<Select
											options={cashboxLayouts}
											value={cashboxLayouts.find(option => option.id === (data.cashbox_layout ?? 'classic'))}
											onChange={(option) => setData({ ...data, 'cashbox_layout': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</div>

								{/* Касса переспрашивает количество выше порога, если сумма строки
								от 1 000 000, — ловит опечатку вроде 230 000 вместо 23, не запрещая банкет
								на 300 порций и не мешая позициям «за 1 сум». 0 — не спрашивать */}
								<div className="d-flex justify-content-between w-100 brand-settings-row brand-settings-row--control">
									<div className="brand-settings-row__label">
										<h6>{t('quantity_confirm_threshold')}</h6>
										<small className="text-muted">{t('quantity_confirm_threshold_hint')}</small>
									</div>
									<div className="brand-settings-row__control">
										<input
											type="text"
											className="form-control"
											name="quantity_confirm_threshold"
											autoComplete="off"
											value={data.quantity_confirm_threshold ?? 100}
											onChange={(e) => setData({
												...data,
												'quantity_confirm_threshold': e.target.value.replace(/[^0-9]/g, ''),
											})} />
									</div>
								</div>

								<h5 className="brand-settings-group">{t('brand_group_shift_salary')}</h5>
								<div className="d-flex justify-content-between w-100 brand-settings-row brand-settings-row--control">
									<div className="brand-settings-row__label">
										<h6>{t('shift_close_with_open_cheques')}</h6>
									</div>
									<div className="brand-settings-row__control">
										<Select
											options={shiftCloseModes}
											value={shiftCloseModes.find(option => option.id === (data.shift_close_mode ?? 'allow'))}
											onChange={(option) => setData({ ...data, 'shift_close_mode': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</div>
								{shiftSettings.map(renderToggle)}

								<h5 className="brand-settings-group">{t('brand_group_guests')}</h5>
								<div className="d-flex justify-content-between w-100 brand-settings-row brand-settings-row--control">
									<div className="brand-settings-row__label">
										<h6>{t('birthday_mode')}</h6>
										<small className="text-muted">{t('birthday_mode_hint')}</small>
									</div>
									<div className="brand-settings-row__control">
										<Select
											options={birthdayModes}
											value={birthdayModes.find(option => option.id === (data.birthday_mode ?? 'off'))}
											onChange={(option) => setData({ ...data, 'birthday_mode': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="col-md-4">
							<div className="con-upload" {...dropProps}>
								<div className="con-img-upload">
									{data.image &&
										<div className="img-upload" style={dropStyle}>
											<button type="button" className="btn-x-file"
												onClick={() => setData({ ...data, 'image': '' })}>
												<i className="uil-times"></i>
											</button>
											<img src={`https://backend.mison.uz/uploads/brands/${data.image}`}
												style={{ maxWidth: 'none', maxHeight: '100%' }} alt={data.image} />
										</div>
									}
									{!data.image &&
										<div className="con-input-upload" style={dropStyle}>
											<input type="file" onChange={(e) => fileUpload(e)} />
											<span className="text-input fz14">
												{t('image2')}
											</span>
										</div>
									}
								</div>
							</div>

							<div className="form-group w-100">
								<label>{t('name2')}</label>
								<input
									type="text"
									className="form-control"
									name="name"
									autoComplete="off"
									value={data.name ?? ''}
									onChange={(e) => handleChangeInput(e)} />
							</div>
							<div className="d-flex gap-2">
								<div className="form-group w-100">
									<label>{t('phone')}</label>
									<input
										type="text"
										className="form-control"
										name="phone"
										autoComplete="off"
										value={data.phone ?? ''}
										onChange={(e) => handleChangeInput(e)} />
								</div>
								<div className="form-group w-100">
									<label>{t('prefix')}</label>
									<input
										type="text"
										className="form-control"
										name="prefix"
										autoComplete="off"
										value={data.prefix ?? ''}
										onChange={(e) => handleChangeInput(e)} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Соцсети и контакты сохраняются построчно, отдельно от кнопки «Сохранить» вверху */}
			<BrandLinks />

			{/* Баннеры — одна библиотека на сайт, приложение, бота и самозаказ; тоже сохраняются по одному */}
			<div className="mt-3">
				<BrandBanners />
			</div>
		</>
	)
}

export default Template